import {Component, computed, inject, input} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {NgxEchartsModule} from 'ngx-echarts';
import {formatAppNumber} from '@frontend/shared';
import {ProviderColumn} from '../table/provider-results-table/provider-results-table.types';
import {SubcriteriaAttributeScoreData} from './subcriteria-attribute-scores.types';
import {
  HeatmapCellData,
  SubcriteriaAttributeScoresHeatmapCalculationService,
} from './subcriteria-attribute-scores-heatmap-calculation.service';
import {getHeatmapCellColor} from './subcriteria-attribute-scores-heatmap.utils';

@Component({
  selector: 'app-subcriteria-attribute-scores-heatmap',
  imports: [NgxEchartsModule],
  templateUrl: './subcriteria-attribute-scores-heatmap.component.html',
  styleUrls: ['./subcriteria-attribute-scores-heatmap.component.scss'],
})
export class SubcriteriaAttributeScoresHeatmapComponent {
  private readonly calculationService = inject(SubcriteriaAttributeScoresHeatmapCalculationService);
  private readonly transloco = inject(TranslocoService);

  readonly rows = input.required<SubcriteriaAttributeScoreData[]>();
  readonly selectedProviders = input.required<ProviderColumn[]>();
  readonly allProviders = input.required<ProviderColumn[]>();
  readonly isExponentialScale = input<boolean>(false);
  readonly rfdMode = input(false);

  private readonly documentStyle = getComputedStyle(document.documentElement);
  private readonly textColor = this.documentStyle.getPropertyValue('--p-text-color').trim();
  private readonly axisLineColor = this.documentStyle.getPropertyValue('--p-surface-600').trim();
  private readonly colors = {
    green: this.documentStyle.getPropertyValue('--color-green').trim() || '#22c55e',
    yellow: this.documentStyle.getPropertyValue('--color-yellow').trim() || '#eab308',
    red: this.documentStyle.getPropertyValue('--color-red').trim() || '#ef4444',
  };

  protected readonly chartHeight = computed(() => {
    const minHeight = 320;
    return Math.max(minHeight, this.rows().length * 44 + 140);
  });

  protected readonly chartOptions = computed(() => {
    const scaleFactor = this.isExponentialScale() ? 16 : 4;
    const xLabels = this.selectedProviders().map((provider) => provider.translatedName);
    const yLabels = this.rows().map((row) => (row.rowCode ? row.rowCode : `SC ${row.scaNumber}`));

    const scoreData = this.calculationService.calculateHeatmapData(
      this.rows(),
      this.selectedProviders(),
      this.allProviders(),
      this.isExponentialScale(),
      this.rfdMode(),
    );

    return {
      animation: false,
      grid: {
        left: 110,
        right: 30,
        top: 50,
        bottom: 60,
        containLabel: true,
      },
      tooltip: {
        confine: true,
        appendToBody: true,
        backgroundColor: this.documentStyle.getPropertyValue('--p-surface-800').trim(),
        borderColor: this.documentStyle.getPropertyValue('--p-surface-600').trim(),
        textStyle: {
          color: this.textColor,
        },
        formatter: (params: {data?: HeatmapCellData}) =>
          this.formatTooltip(params.data, scaleFactor, this.rfdMode()),
      },
      xAxis: {
        type: 'category',
        data: xLabels,
        position: 'top',
        axisLabel: {
          color: this.textColor,
          interval: 0,
          rotate: 25,
        },
        axisLine: {lineStyle: {color: this.axisLineColor}},
        axisTick: {lineStyle: {color: this.axisLineColor}},
      },
      yAxis: {
        type: 'category',
        data: yLabels,
        inverse: true,
        axisLabel: {
          color: this.textColor,
          width: 240,
          overflow: 'truncate',
        },
        axisLine: {lineStyle: {color: this.axisLineColor}},
        axisTick: {lineStyle: {color: this.axisLineColor}},
      },
      visualMap: {
        min: 0,
        max: 1,
        orient: 'horizontal',
        left: 'center',
        bottom: 10,
        seriesIndex: 0,
        inRange: {
          color: [this.colors.red, this.colors.yellow, this.colors.green],
        },
        textStyle: {
          color: this.textColor,
        },
      },
      series: [
        {
          name: 'Score',
          type: 'heatmap',
          data: scoreData,
          itemStyle: {
            borderColor: this.axisLineColor,
            borderWidth: 1,
          },
          label: {
            show: true,
            formatter: (params: {data?: HeatmapCellData}) => {
              const data = params.data;
              if (!data) return '';
              const value = this.formatBadgeValue(data, scaleFactor, this.rfdMode());
              return value ? `{badge|${value}}` : '';
            },
            rich: {
              badge: {
                backgroundColor: this.documentStyle.getPropertyValue('--p-surface-800').trim(),
                borderColor: this.documentStyle.getPropertyValue('--p-surface-600').trim(),
                borderWidth: 1,
                borderRadius: 4,
                padding: [4, 8],
                color: this.textColor,
                fontSize: 12,
                fontWeight: 600,
              },
            },
          },
          emphasis: {
            itemStyle: {
              borderColor: this.axisLineColor,
              borderWidth: 1,
            },
          },
        },
      ],
    };
  });

  private formatTooltip(data?: HeatmapCellData, scaleFactor = 1, rfdMode = false): string {
    if (!data) return '';

    const providerLabel = this.transloco.translate('common.provider');
    const scoreLabel = this.transloco.translate('common.score');
    const gapLabel = this.transloco.translate('common.gap');
    const title = `<div style="font-weight: bold; font-size: 14px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid rgba(255,255,255,0.2);">${data.attributeLabel}</div>`;
    const providerValue = this.formatProviderValue(data.providerValue);
    const scoreValue = this.formatTableScoreValue(data, scaleFactor, rfdMode);
    const gapDisplay = this.formatGapValue(data, scaleFactor, rfdMode);

    const importanceBlock = rfdMode
      ? ''
      : `<div style="margin-bottom: 6px;">
          <span style="font-weight: 600;">${this.transloco.translate('common.importance')}:</span>
          <span style="margin-left: 8px;">${data.importanceLabel}${data.importanceIcon ? `<i class="${data.importanceIcon}" style="margin-left: 6px;"></i>` : ''}</span>
        </div>`;

    const cellColor = getHeatmapCellColor(data.value[2], this.colors);
    const gapBlock =
      gapDisplay !== undefined
        ? `<div style="margin-bottom: 6px;">
            <span style="font-weight: 600;">${gapLabel}:</span>
            <span style="margin-left: 8px;">${gapDisplay}</span>
            <span style="display: inline-block; width: 10px; height: 10px; background-color: ${cellColor}; border-radius: 4px; margin-left: 6px; vertical-align: middle; box-shadow: 0 0 6px ${cellColor}66;"></span>
          </div>`
        : '';

    return `
      ${title}
      <div style="margin-top: 8px;">
        ${importanceBlock}
        <div style="margin-bottom: 6px;">
          <span style="font-weight: 600;">${providerLabel}:</span>
          <span style="margin-left: 8px;">${providerValue}</span>
        </div>
        <div style="margin-bottom: 6px;">
          <span style="font-weight: 600;">${scoreLabel}:</span>
          <span style="margin-left: 8px;">${scoreValue}</span>
        </div>
        ${gapBlock}
      </div>
    `;
  }

  private formatProviderValue(value?: number): string {
    if (value === null || value === undefined) return '-';
    return formatAppNumber(value, this.transloco.getActiveLang());
  }

  /** Badge: shows gap * scale (non-RFD) or raw value (RFD). */
  private formatBadgeValue(data: HeatmapCellData, scaleFactor: number, rfdMode: boolean): string {
    if (rfdMode) {
      return data.providerValue != null ? data.providerValue.toFixed(1) : '';
    }
    if (data.gapValue !== undefined && data.gapValue !== null) {
      const displayValue = data.gapValue * scaleFactor;
      return displayValue.toFixed(2);
    }
    return '';
  }

  /** Tooltip gap line: same value as badge (gap * scale). */
  private formatGapValue(
    data: HeatmapCellData,
    scaleFactor: number,
    rfdMode: boolean,
  ): string | undefined {
    if (rfdMode || data.gapValue === undefined || data.gapValue === null) return undefined;
    return (data.gapValue * scaleFactor).toFixed(2);
  }

  /** Tooltip score: table-compatible calculation (matches provider-results-table). */
  private formatTableScoreValue(
    data: HeatmapCellData,
    scaleFactor: number,
    rfdMode: boolean,
  ): string {
    if (rfdMode) {
      return data.providerValue != null ? data.providerValue.toFixed(1) : '-';
    }
    if (data.tableScoreValue !== undefined && data.tableScoreValue !== null) {
      return data.tableScoreValue.toFixed(2);
    }
    return '-';
  }
}
